// ◉ xlVISION · Powering Business Together!
// © ExcelVision, Lda. — Braga, Portugal
// ( ·:·●●·:· ) xlVISION | Powering Business Together!
//
// ============================================================================
// ONDE O PEDIDO TERMINA, no ecrã — a MESMA resposta para o motor e para a reserva
// ============================================================================
//
// 📗 Cliente, 2026-09-29: *«quando não se sabe, o melhor é assumir o do dia anterior e colocar
//    um ponto de interrogação»*.
//
// ⚠️⚠️ **O achado que isto fecha: a reserva JÁ gravava uma presunção, escondida.** O
//    `PainelPlanear` enviava `localidadeFimId: pedido.largada?.localidade ?? destino` — sem
//    largada, a cidade da RECOLHA no último dia —, enquanto o motor recebia `''` e não verificava
//    a saída nem a rotação espelho. Duas portas com duas respostas: a da reserva era uma
//    presunção que ninguém via, a do motor era «não sei».
//
// ✅ **Uma só formulação:** o motor (a query e o contexto do condutor), a reserva
//    (`localidadeFimId`), o arrasto e a linha «largada do grupo» chamam `destinoFinalDe`, que
//    devolve `Pedido.destinoFinal` — calculado pelo servidor com `destinoFinalDoPedido`. A
//    presunção **diz-se junto à decisão** (D-007: avisa e não impede) com `avisoDoDestinoPresumido`.
//
// ⚠️ **A HORA NUNCA SE PRESUME:** isto devolve só a cidade. A hora e o voo continuam a ler-se de
//    `pedido.largada`, e ficam em branco quando não há.
// ============================================================================

import {
  destinoFinalDoPedido,
  rotuloDoDestinoFinal,
  TITULO_DESTINO_PRESUMIDO,
  type DestinoFinal,
} from '@xlv-gva-mpt/dominio';
import type { Pedido, PontoPedido } from './pedido.js';

/**
 * Onde o pedido termina — o `destinoFinal` do servidor.
 *
 * ⚠️ **Sem o campo** (um servidor anterior, ou um pedido montado à mão) recalcula-se pela MESMA
 *    função sobre os dois pontos que o ecrã tem: a largada registada continua registada, e sem ela
 *    a recolha entra como `presumida`. Não se lê `largada.localidade` por conta própria — era a
 *    segunda formulação que o guarda `leitores-da-largada.test.ts` recusa.
 */
export function destinoFinalDe(
  pedido: Pick<Pedido, 'destinoFinal' | 'recolha' | 'largada'>,
): DestinoFinal | null {
  if (pedido.destinoFinal !== undefined) return pedido.destinoFinal;
  return destinoFinalDoPedido(
    [pedido.recolha, pedido.largada].filter((p): p is PontoPedido => p !== null),
  );
}

/** O código da localidade onde o pedido termina, ou `''` — o que o motor e o arrasto enviam. */
export function codigoDoDestinoFinal(pedido: Pick<Pedido, 'destinoFinal' | 'recolha' | 'largada'>): string {
  return destinoFinalDe(pedido)?.localidade ?? '';
}

// ⚠️ A frase do aviso vive no domínio (`avisoDoDestinoPresumido`) — a MESMA que o motor usa
//    no encaixe. Reexporta-se para quem já a importava daqui.
export { avisoDoDestinoPresumido } from '@xlv-gva-mpt/dominio';
